/**
 * GUTTER / DOWNSPOUT SIZING (docs/bim-plan/14 slice E, Matt's resolved Q9):
 * plumbing-code capacity tables + the design-rainfall setting.
 *
 * ============================ PROVISIONAL ============================
 * Every constant in this file ships PROVISIONAL (the lineworkStyle phase-color
 * precedent): the capacity rows follow the California Plumbing Code Chapter 11
 * family of tables (roof gutters at 1/8"-per-foot slope; vertical rainwater
 * leaders), normalized to a 1 in/hr rainfall basis — CONFIRM the exact values,
 * edition, and the firm's plan-check basis before relying on a printed sheet.
 * The default design rainfall (Laguna Beach / coastal Orange County 100-yr 1-hr)
 * is likewise provisional; it's a PER-PROJECT setting (siteSettings), so dialing
 * in the jurisdiction's real number is one field, not a code change.
 * =====================================================================
 *
 * Sizing method (the code's own): capacity tables are stated at 1 in/hr —
 * a run's EFFECTIVE area is its horizontally-projected tributary area × the
 * design rainfall, looked up against the 1 in/hr row. Gutter slope is assumed
 * 1/8" per foot (the table row the firm details); steeper gutters only gain
 * capacity, so the assumption is conservative.
 */

/** PROVISIONAL — Laguna Beach 100-yr 1-hr design rainfall, in/hr */
export const DEFAULT_DESIGN_RAINFALL_IN_HR = 1.5

/** semicircular gutter at 1/8"/ft slope: [diameter in, max projected SF at 1 in/hr] */
export const GUTTER_CAPACITY_SF: [number, number][] = [
  [3, 680],
  [4, 1440],
  [5, 2500],
  [6, 3840],
  [7, 5520],
  [8, 7960],
]

/** vertical rainwater leader (downspout): [diameter in, max projected SF at 1 in/hr] */
export const LEADER_CAPACITY_SF: [number, number][] = [
  [2, 2880],
  [3, 8800],
  [4, 18400],
  [5, 34600],
  [6, 54000],
]

function sizeFor(table: [number, number][], areaSf: number, rainfallInHr: number): number | null {
  const effective = areaSf * Math.max(0.1, rainfallInHr)
  for (const [sizeIn, capSf] of table) {
    if (effective <= capSf) return sizeIn
  }
  return null   // exceeds the table — the callout prints OVER, never a silent max
}

/** smallest code-adequate gutter diameter (in), or null when off the table */
export const gutterSizeIn = (areaSf: number, rainfallInHr: number): number | null =>
  sizeFor(GUTTER_CAPACITY_SF, areaSf, rainfallInHr)

/** smallest code-adequate leader/downspout diameter (in), or null when off the table */
export const leaderSizeIn = (areaSf: number, rainfallInHr: number): number | null =>
  sizeFor(LEADER_CAPACITY_SF, areaSf, rainfallInHr)
